/**
 * リポジトリ実装の配線（DI 入口）。
 *
 * usecase / フックは domain のインターフェース型に依存し、ここで実体を注入する。
 *
 * - 独自メソッドを持つエンティティ: 専用クラス＋専用インターフェース（例: customers）
 * - 素の CRUD で足りるエンティティ: 汎用ファクトリ `repository("table")` で取得（A 方針）
 */
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { SupabaseRepository } from "./SupabaseRepository";
import { SupabaseCustomerRepository } from "./SupabaseCustomerRepository";
import type { CustomerRepository } from "@/domain/repositoryInterface/CustomerRepository";
import type { Repository } from "@/domain/repositoryInterface/Repository";

type Tables = Database["public"]["Tables"];
type TableName = keyof Tables;
type RepoFor<T extends TableName> = Repository<
  Tables[T]["Row"],
  Tables[T]["Insert"],
  Tables[T]["Update"]
>;

// テーブルごとに 1 インスタンスをメモ化して使い回す
const cache = new Map<TableName, unknown>();

/**
 * 任意テーブルの汎用リポジトリを取得する。
 * 独自メソッドが不要なテーブルはこれだけで DB アクセスを Repository に閉じ込められる。
 *
 * 例: `repository("areas").findMany({ where: [{ column: "company_id", op: "eq", value: id }] })`
 */
export function repository<T extends TableName>(table: T): RepoFor<T> {
  let repo = cache.get(table) as RepoFor<T> | undefined;
  if (!repo) {
    repo = new SupabaseRepository<T>(table, supabase) as unknown as RepoFor<T>;
    cache.set(table, repo);
  }
  return repo;
}

// ---- 独自メソッドを持つ名前付きリポジトリ ----
export const customerRepository: CustomerRepository = new SupabaseCustomerRepository();

// bookings は独自メソッド不要のため汎用ファクトリ経由（join 取得は findMany<R>({ select }) で対応）
export const bookingRepository: RepoFor<"bookings"> = repository("bookings");
